import {
  Bell,
  Building2,
  Calendar,
  ClipboardList,
  CreditCard,
  FileText,
  FolderOpen,
  LayoutDashboard,
  Send,
  Settings,
  Shield,
  ShieldCheck,
  Sparkles,
  Tag,
  UserCheck,
  Users,
} from 'lucide-react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';
import { useModoSlim } from '../hooks/useModoSlim';
import { useAtuacao } from '../lib/delegacao/atuacao';
import { CertificadoVencimentoFaixa } from './certificates/CertificadoVencimentoFaixa';
import { AtuacaoFilete, SincronizarAtuacao } from './delegacao/AtuacaoShell';
import SidebarLayout from './SidebarLayout';
import SlimLayout from './SlimLayout';

// Grupos consecutivos viram seções do menu (SidebarLayout.buildSections): a
// ordem aqui é a ordem dos grupos. Os endereços não mudam com os rótulos.
const mainNavItems = [
  { label: 'Painel',           icon: <LayoutDashboard size={20} />, path: '/app',               group: 'Trabalho' },
  { label: 'Acervo',           icon: <FileText size={20} />,        path: '/app/processos',     group: 'Trabalho' },
  { label: 'Petições',         icon: <Send size={20} />,            path: '/app/peticoes',      group: 'Trabalho' },
  { label: 'Comunicações',     icon: <Bell size={20} />,            path: '/app/comunicacoes',  group: 'Trabalho' },
  { label: 'Secretário de IA', icon: <Sparkles size={20} />,        path: '/app/secretario-ia', group: 'Trabalho' },
  { label: 'Clientes',         icon: <Users size={20} />,           path: '/app/clientes',      group: 'Gestão' },
  { label: 'Documentos',       icon: <FolderOpen size={20} />,      path: '/app/documentos',    group: 'Gestão' },
  { label: 'Modelos',          icon: <ClipboardList size={20} />,   path: '/app/modelos',       group: 'Gestão' },
  { label: 'Tags',             icon: <Tag size={20} />,             path: '/app/tags',          group: 'Gestão' },
  { label: 'Calendário',       icon: <Calendar size={20} />,        path: '/app/calendario',    group: 'Gestão' },
  { label: 'Certificados',     icon: <ShieldCheck size={20} />,     path: '/app/certificados',  group: 'Conta' },
  { label: 'Escritórios',      icon: <Building2 size={20} />,       path: '/app/escritorios',   group: 'Conta' },
  { label: 'Delegações',       icon: <UserCheck size={20} />,       path: '/app/delegacoes',    group: 'Conta' },
  { label: 'Assinatura',       icon: <CreditCard size={20} />,      path: '/app/assinatura',    group: 'Conta' },
  { label: 'Configurações',    icon: <Settings size={20} />,        path: '/app/configuracoes', group: 'Conta' },
];

// Na conta de outra pessoa só as telas do que foi delegado (docs/PLANO_DELEGACAO.md §6).
// O backend é quem nega (fail-closed); esconder o resto evita a tela que só responde 403.
const PREFIXOS_DELEGAVEIS = [
  '/app/clientes',
  '/app/processos',
  '/app/peticoes',
  '/app/comunicacoes',
  '/app/documentos',
  '/app/calendario',
  // Tags: Leitura só vê; a aba Modelos some (modelos não são delegáveis).
  '/app/tags',
  // Os feriados e suspensões da conta (o atalho do Calendário); o resto de Configurações é da pessoa.
  '/app/configuracoes/feriados',
];

function rotaDelegavel(pathname: string): boolean {
  return pathname === '/app' || pathname === '/app/' || PREFIXOS_DELEGAVEIS.some((p) => pathname.startsWith(p));
}

// Rotas acessíveis antes de completar o onboarding
// Escritórios e Delegações: o secretário convidado (sem OAB) aceita o convite e escolhe a
// conta em que vai atuar sem precisar passar pelo cadastro de advogado.
const ONBOARDING_EXEMPT_PATHS = ['/app/onboarding', '/app/configuracoes', '/app/escritorios', '/app/delegacoes'];

function Layout() {
  const { user } = useAuth();
  const location = useLocation();
  const modoSlim = useModoSlim();
  const atuacao = useAtuacao();
  const isAdminOrSupport = user?.role === 'admin' || user?.role === 'suporte';

  // Petições aparece para TODO perfil. Montar a peça (redigir, anexar, salvar,
  // baixar em PDF/DOCX) é liberado a quem não é advogado — só o protocolo exige
  // a inscrição na OAB (`docs/PETICIONAMENTO.md` §4.1). Esconder o item deixava
  // os rascunhos desse usuário inalcançáveis: a própria tela de peticionar diz
  // "fica em Petições → Continuar rascunho".
  // Secretário de IA é estritamente do advogado (backend exige role advogado).
  const isAdvogado = user?.role === 'advogado';
  const navItems = (atuacao ? mainNavItems.filter((i) => rotaDelegavel(i.path)) : mainNavItems).filter(
    (item) => item.path !== '/app/secretario-ia' || isAdvogado,
  );
  // Link do e-mail de intimações (`?atuando=`): o `SincronizarAtuacao` do shell entra na
  // conta do titular — nenhum gate pode desviar a navegação antes disso.
  const entrandoPorLink = new URLSearchParams(location.search).has('atuando');

  // Na conta de outra pessoa, tela não delegada volta para a visão geral dela.
  if (atuacao && !rotaDelegavel(location.pathname)) {
    return <Navigate to="/app" replace />;
  }

  // Redirecionar para onboarding se ainda não foi completado (na própria conta:
  // o secretário que só atua na conta do titular não precisa cadastrar OAB).
  if (
    !atuacao &&
    !entrandoPorLink &&
    user &&
    user.onboarding_completado === false &&
    !ONBOARDING_EXEMPT_PATHS.some((p) => location.pathname.startsWith(p))
  ) {
    return <Navigate to="/app/onboarding" replace />;
  }

  // Aba dedicada (`?slim=1`): mesma rota, sem menu principal. Fica DEPOIS do
  // gate de onboarding — quem ainda não completou o cadastro não escapa dele
  // abrindo um link em outra aba.
  if (modoSlim) return <SlimLayout key={atuacao?.titularId ?? 'propria'} />;

  // `key`: trocar de conta remonta o conteúdo inteiro — nenhum estado local de tela
  // (filtros, seleção, rascunho em edição) atravessa de uma conta para a outra.
  return (
    <SidebarLayout
      emPainel
      key={atuacao?.titularId ?? 'propria'}
      banner={
        <>
          <SincronizarAtuacao />
          <AtuacaoFilete />
          <CertificadoVencimentoFaixa />
        </>
      }
      navItems={navItems}
      footerLink={
        isAdminOrSupport
          ? { label: 'Administração', icon: <Shield size={20} />, path: '/app/admin' }
          : undefined
      }
    />
  );
}

export default Layout;
